const DISCARDED_ITEM_STATUSES = new Set([
  "VOID",
  "RETURN",
  "CANCELED",
  "CANCELLED",
  "REJECTED",
  "REJECTED_BY_ORDERS_CO",
]);

const COMPED_ITEM_STATUSES = new Set(["COMP_AFTER_TXN", "COMP_BEFORE_TXN"]);

const PRICE_TOLERANCE = 0.01;

const roundMoney = (value) => Math.round(value * 100) / 100;

const isCompedItem = (item) => COMPED_ITEM_STATUSES.has(item?.status?.name);

const isPromotionAttributableItem = (item) =>
  !item?.status?.name || !DISCARDED_ITEM_STATUSES.has(item.status.name);

const matchesQty = (affected, line) => {
  const lineQty = line.count || 0;
  return (affected.qty ?? lineQty) === lineQty;
};

const matchesQtyAndPrice = (affected, line) => {
  if (!matchesQty(affected, line)) {
    return false;
  }
  const linePrice = roundMoney(line.price || 0);
  const affectedPrice = roundMoney(affected.originalPrice ?? linePrice);
  return Math.abs(linePrice - affectedPrice) <= PRICE_TOLERANCE;
};

function addDiscount(perLine, lineUuid, amount) {
  perLine.set(lineUuid, (perLine.get(lineUuid) ?? 0) + Math.abs(amount));
}

function prorateByCount(perLine, lines, amount) {
  const totalCount = lines.reduce((sum, line) => sum + (line.count || 1), 0);
  if (totalCount <= 0) {
    return;
  }
  let remaining = roundMoney(amount);
  lines.forEach((line, index) => {
    const isLast = index === lines.length - 1;
    const share = isLast
      ? remaining
      : roundMoney(((line.count || 1) / totalCount) * amount);
    remaining = roundMoney(remaining - share);
    if (share > 0 && line.uuid) {
      addDiscount(perLine, line.uuid, share);
    }
  });
}

function attributePromotionToLines(promotion, linesByMenuItem) {
  const perLine = new Map();
  const affectedByMenuItem = new Map();

  for (const affected of promotion.affectedItems ?? []) {
    const menuItemUuid = affected?.itemUuid;
    if (!menuItemUuid || !linesByMenuItem.has(menuItemUuid)) {
      continue;
    }
    const group = affectedByMenuItem.get(menuItemUuid);
    if (group) {
      group.push(affected);
    } else {
      affectedByMenuItem.set(menuItemUuid, [affected]);
    }
  }

  for (const [menuItemUuid, affectedItems] of affectedByMenuItem) {
    const lines = linesByMenuItem.get(menuItemUuid) ?? [];
    const claimed = new Set();
    let pending = affectedItems;

    for (const matches of [matchesQtyAndPrice, matchesQty]) {
      if (!pending.length) {
        break;
      }
      const stillPending = [];
      for (const affected of pending) {
        const line = lines.find(
          (candidate) =>
            !!candidate.uuid &&
            !claimed.has(candidate.uuid) &&
            matches(affected, candidate)
        );
        if (!line?.uuid) {
          stillPending.push(affected);
          continue;
        }
        claimed.add(line.uuid);
        addDiscount(perLine, line.uuid, affected.discountAmount || 0);
      }
      pending = stillPending;
    }

    if (pending.length) {
      const leftover = pending.reduce(
        (sum, affected) => sum + Math.abs(affected.discountAmount || 0),
        0
      );
      const unclaimed = lines.filter(
        (line) => !!line.uuid && !claimed.has(line.uuid)
      );
      prorateByCount(perLine, unclaimed.length ? unclaimed : lines, leftover);
    }
  }

  for (const [lineUuid, amount] of perLine) {
    perLine.set(lineUuid, roundMoney(amount));
  }
  return perLine;
}

function groupAttributableLinesByMenuItem(items) {
  const linesByMenuItem = new Map();
  const compedLines = [];

  for (const item of items) {
    if (!item?.uuid || !item.itemUuid || !isPromotionAttributableItem(item)) {
      continue;
    }
    if (isCompedItem(item)) {
      compedLines.push(item);
      continue;
    }
    const lines = linesByMenuItem.get(item.itemUuid);
    if (lines) {
      lines.push(item);
    } else {
      linesByMenuItem.set(item.itemUuid, [item]);
    }
  }

  for (const item of compedLines) {
    const lines = linesByMenuItem.get(item.itemUuid);
    if (lines) {
      lines.push(item);
    } else {
      linesByMenuItem.set(item.itemUuid, [item]);
    }
  }

  return linesByMenuItem;
}

export function buildPromotionAdjustmentsByLineUuid(items, promotions) {
  const adjustmentsByLineUuid = new Map();
  if (!items?.length || !promotions?.length) {
    return adjustmentsByLineUuid;
  }

  const linesByMenuItem = groupAttributableLinesByMenuItem(items);
  if (!linesByMenuItem.size) {
    return adjustmentsByLineUuid;
  }

  for (const promotion of promotions) {
    const perLine = attributePromotionToLines(promotion, linesByMenuItem);
    for (const [lineUuid, discountAmount] of perLine) {
      if (discountAmount <= 0) {
        continue;
      }
      const adjustments = adjustmentsByLineUuid.get(lineUuid) ?? [];
      adjustments.push({
        promotionName: promotion.promotionName,
        displayLabel:
          promotion.displayLabel || promotion.promotionName || "Promotion",
        discountAmount,
      });
      adjustmentsByLineUuid.set(lineUuid, adjustments);
    }
  }

  return adjustmentsByLineUuid;
}

export function buildBillablePromotionAmounts(items, promotions) {
  if (!promotions?.length) {
    return [];
  }
  const wholeAmounts = promotions.map((promotion) =>
    Math.abs(promotion.discountAmount ?? 0)
  );
  if (!items?.length) {
    return wholeAmounts;
  }

  const compedLineUuids = new Set(
    items.filter((item) => item?.uuid && isCompedItem(item)).map((item) => item.uuid)
  );
  if (!compedLineUuids.size) {
    return wholeAmounts;
  }

  const linesByMenuItem = groupAttributableLinesByMenuItem(items);
  return promotions.map((promotion, index) => {
    const perLine = attributePromotionToLines(promotion, linesByMenuItem);
    if (!perLine.size) {
      return wholeAmounts[index];
    }
    let billable = 0;
    for (const [lineUuid, amount] of perLine) {
      if (!compedLineUuids.has(lineUuid)) {
        billable += amount;
      }
    }
    return roundMoney(billable);
  });
}

export function orderItemsFromCategories(categories) {
  const items = [];
  for (const category of categories || []) {
    for (const item of category?.items || []) {
      if (item) {
        items.push(item);
      }
    }
  }
  return items;
}
